import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, CloudOff, Plus, RefreshCw } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { listFlags, recordAttempt } from './api'
import { Pill, count, duration, flagRuleLabel, flagSeverityTone, responseStatusLabel } from './vocab'
import { uploadHeldClips, useRecorder } from './AudioRecorder'
import type { CaptureSnapshot } from './captureSession'

/**
 * What actually happened to this interview.
 *
 * ## Three outcomes, and none of them is "done"
 *
 * **Stored.** The server has it, and the panel says what it did with it — how
 * many answers it counted, how many the branches skipped, how many were shown
 * and left blank, and which integrity flags it raised. A `GPS_MISSING` flag on
 * an interview whose fix was refused is not a failure of this screen; it is the
 * correct record of what happened, and the interviewer should see it while they
 * still remember why.
 *
 * **Queued.** The device could not reach the server, so the interview is on this
 * phone and will go up on its own. That is a different sentence from "saved", and
 * conflating them is how a phone gets wiped with a day's fieldwork on it.
 *
 * **Refused.** The server read it and said no. Its sentence is rendered verbatim
 * — it is composed to say what to do next — and where that sentence names an
 * action this screen can take, the button is right there.
 *
 * ## A repeated key is not a duplicate warning
 *
 * The server returns the ORIGINAL interview for a key it has seen. So a
 * successful retry may be showing an interview that was stored minutes ago, and
 * the honest thing to say is "this is the same interview, not a second one" —
 * not a warning, which would send an interviewer looking for a duplicate to
 * delete.
 */
export function CaptureSummary({
  session, onNext,
}: {
  session: CaptureSnapshot
  onNext: () => void
}) {
  const result = session.result
  const rec = useRecorder()

  const flags = useLoad(
    () => (result ? listFlags(result.responseId) : Promise.resolve([])),
    [result?.responseId],
  )

  if (session.queued) {
    return (
      <Panel title="Held on this device" description="Nothing is lost. Nothing is sent yet.">
        <div className="space-y-3">
          <Note tone="info">
            <CloudOff className="mr-1.5 inline h-3.5 w-3.5" />
            This interview could not reach the server, so it is queued on this phone with its
            idempotency key. It goes up by itself the moment there is signal, and because the
            key travels with it, a copy that did somehow land is returned rather than stored
            twice.
          </Note>
          {rec.clips.length > 0 && (
            <p className="text-erp-xs text-muted-foreground">
              The recording is held too, and uploads after the interview is accepted — audio
              cannot be attached to an interview the server does not have yet.
            </p>
          )}
          <NextActions onNext={onNext} />
        </div>
      </Panel>
    )
  }

  if (!result) {
    return (
      <Panel title="Not submitted">
        <div className="space-y-3">
          <Note tone="bad">
            {session.refusal ?? 'This interview has not been submitted.'}
          </Note>
          <RefusalActions session={session} />
        </div>
      </Panel>
    )
  }

  const r = result.detail.response

  return (
    <div className="space-y-4">
      <Panel
        title="Interview stored"
        description={`${r.surveyVersionId ? 'Recorded against the published instrument. ' : ''}${fmtDateTime(r.submittedAt)}`}
        right={<Pill>{responseStatusLabel(r.statusCode)}</Pill>}
      >
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-erp-sm">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>
              {result.retried
                ? 'Saved. This was a retry, and the key it carried is the one the first attempt '
                  + 'used — so the server returned the interview it already had rather than '
                  + 'creating a second one. There is one respondent here, not two.'
                : 'Saved.'}
            </span>
          </div>

          <dl className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
            <Cell label="Disposition" value={r.dispositionCode} />
            <Cell label="Answered" value={count(r.answeredCount)} />
            {/* Skipped and blank are DIFFERENT facts and are never summed here:
                one is the instrument branching as designed, the other is a
                question that was asked and got nothing. */}
            <Cell label="Skipped by a branch" value={count(r.skippedCount)} />
            <Cell label="Shown, left blank" value={count(r.blankCount)} />
            <Cell label="Duration" value={duration(r.durationSeconds)} />
            <Cell label="Unit" value={r.unitName ?? '—'} />
          </dl>

          <p className="text-erp-xs text-muted-foreground">
            Identifiers are masked on every read: {r.respondentNameMask ?? '—'}
            {r.respondentPhoneMask ? ` · ${r.respondentPhoneMask}` : ''}. Seeing them back
            needs <code>survey:read-raw</code>.
          </p>

          <GeoLine session={session} />
        </div>
      </Panel>

      <Panel
        title="Recording"
        description="The interview is saved whatever this says."
        right={rec.clips.some((c) => c.state === 'failed') ? (
          <button type="button" className="erp-btn h-9 text-erp-xs"
            onClick={() => { void uploadHeldClips(result.responseId) }}>
            <RefreshCw className="h-3 w-3" /> Retry the upload
          </button>
        ) : undefined}
      >
        {rec.clips.length === 0 ? (
          <p className="text-erp-sm text-muted-foreground">No clip was recorded.</p>
        ) : (
          <ul className="space-y-1.5 text-erp-sm">
            {rec.clips.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2">
                <Pill>{c.kindCode === 'AUDIO_CONSENT' ? 'Consent' : c.kindCode === 'PHOTO' ? 'Photo' : 'Interview'}</Pill>
                <span className={c.state === 'stored' ? 'text-emerald-700'
                  : c.state === 'refused' ? 'text-red-700' : 'text-muted-foreground'}>
                  {c.state === 'stored' ? 'Uploaded'
                    : c.state === 'uploading' ? 'Uploading…'
                      : c.state === 'refused' ? 'Refused'
                        : c.state === 'failed' ? 'Upload failed — it will be retried'
                          : 'Waiting to upload'}
                </span>
                {c.message && (
                  <span className="w-full text-erp-xs text-muted-foreground">{c.message}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="What the server flagged"
        description="Raised on submission, from the interview itself."
      >
        <ErrorStrip message={flags.error} />
        {flags.loading && !flags.data ? (
          <LoadingBlock label="Reading flags…" />
        ) : (flags.data ?? []).length === 0 ? (
          <p className="text-erp-sm text-muted-foreground">Nothing flagged.</p>
        ) : (
          <ul className="space-y-2">
            {(flags.data ?? []).map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-2 text-erp-sm">
                <Pill tone={flagSeverityTone(f.severityCode)}>{f.severityCode}</Pill>
                <span>{flagRuleLabel(f.ruleCode)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-erp-xs text-muted-foreground">
          A flag is a question for a supervisor, not an accusation and not a rejection. It is
          shown here because the person who can explain it is standing in the place it is about.
        </p>
      </Panel>

      <Panel>
        <NextActions onNext={onNext} />
      </Panel>
    </div>
  )
}

function GeoLine({ session }: { session: CaptureSnapshot }) {
  const place = session.place
  if (place?.ok) {
    return (
      <p className="text-erp-xs text-muted-foreground">
        Location sent with the interview, accurate to about {place.location.accuracyM ?? '—'} m.
      </p>
    )
  }
  return (
    <p className="text-erp-xs text-amber-700">
      No location was sent — {place?.reason ?? 'no fix was taken'} The server records this as
      GPS_MISSING, which is the honest outcome: an interview with no fix is reviewable, and a
      coordinate typed in to satisfy a form would not be.
    </p>
  )
}

/**
 * The actions the server's refusal names.
 *
 * `survey/consent-required` is the one that matters. The server's sentence says
 * to log a REFUSAL contact attempt instead — that is the record that keeps the
 * refusal in the response rate — so the button that does it is here rather than
 * two screens away, where it would not get pressed.
 */
function RefusalActions({ session }: { session: CaptureSnapshot }) {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canLog = session.refusalCode === 'consent-required'
    && session.surveyId !== null && session.assignmentId !== null

  async function logRefusal() {
    if (!session.surveyId || !session.assignmentId) return
    setBusy(true)
    setError(null)
    try {
      await recordAttempt(session.surveyId, session.assignmentId, {
        outcomeCode: 'REFUSAL',
        attemptedAt: new Date().toISOString(),
        note: 'Consent was declined at the door.',
      })
      setDone(true)
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <ErrorStrip message={error} />
      {canLog && !done && (
        <button type="button" className="erp-btn erp-btn-primary h-11" disabled={busy}
          onClick={logRefusal}>
          <Plus className="h-4 w-4" /> Log this refusal as a contact attempt
        </button>
      )}
      {done && (
        <Note tone="good">
          Logged. The refusal now counts in this poll's cooperation and refusal rates, which is
          where a supervisor can see it.
        </Note>
      )}
      {!canLog && !done && (
        <p className="text-erp-xs text-muted-foreground">
          {session.assignmentId
            ? 'Nothing on this screen can clear that refusal. Fix what it names and submit again '
              + '— the interview keeps the same key, so a retry cannot become a second respondent.'
            : 'This interview was opened without an assignment, so there is no assignment to log '
              + 'a contact attempt against. Open it from My work to record one.'}
        </p>
      )}
    </div>
  )
}

function NextActions({ onNext }: { onNext: () => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="erp-btn erp-btn-primary h-12 flex-1 min-w-[12rem]"
        onClick={onNext}>
        Start the next interview
      </button>
      <Link to="/polling/my-work" className="erp-btn h-12 flex-1 min-w-[10rem]">
        Back to my work
      </Link>
    </div>
  )
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-erp-md tabular-nums">{value}</dd>
    </div>
  )
}
